// MSW request handlers that stand in for the Go server in server/.
// Every route mirrors a real `/api/v2/...` endpoint listed in
// webapp/src/octoClient.ts; the responses are built from src/mocks/db.ts.

import {http, HttpResponse} from 'msw'

import {Block, createBlock} from '../types/block'
import {Board, createBoard} from '../types/board'
import {CategoryBoards} from '../types/category'
import {IUser, defaultClientConfig} from '../types/user'
import {Utils, IDType} from '../common/utils'
import {
    addUser,
    allUsers,
    categoriesOfTeam,
    checkCredentials,
    createSession,
    deleteBoard,
    deleteBlock,
    findBoard,
    findUser,
    insertBlock,
    patchBoard,
    patchBlock,
    queryBlocks,
    resetDatabase,
    boardsOfTeam,
    undeleteBlock,
    upsertBoard,
    upsertCategory,
    userForToken,
} from './db'
import {TEAM_ID} from './seed'

type Json = Record<string, any>

const json = (body: unknown, init?: ResponseInit) => HttpResponse.json(body as any, init)

const notFound = (id = 'not-found') => json({error: id}, {status: 404})
const unauthorized = () => json({error: 'not-logged-in'}, {status: 401})

function authed(request: Request): IUser | undefined {
    const header = request.headers.get('Authorization') || ''
    const token = header.startsWith('Bearer ') ? header.slice(7) : ''
    const userId = token ? userForToken(token) : undefined
    return userId ? findUser(userId) : undefined
}

async function readBody(request: Request): Promise<Json> {
    try {
        return (await request.json()) as Json
    } catch {
        return {}
    }
}

/** GET /api/v2/teams/:teamId/blocks?parent_id=&type= (team-scoped read) */
function teamBlocks(request: Request) {
    const url = new URL(request.url)
    const parentId = url.searchParams.get('parent_id') || undefined
    const type = url.searchParams.get('type') || undefined
    return queryBlocks({parentId, type})
}

export const handlers = [
    // ---- auth -------------------------------------------------------------

    http.post('/api/v2/login', async ({request}) => {
        const {username, password} = await readBody(request)
        const user = checkCredentials(String(username || ''), String(password || ''))
        if (!user) {
            return json({error: 'invalid-credentials'}, {status: 401})
        }
        return json({token: createSession(user.id)})
    }),

    http.post('/api/v2/logout', () => json({})),

    http.post('/api/v2/register', async ({request}) => {
        const body = await readBody(request)
        const username = String(body.username || '').trim()
        const email = String(body.email || '').trim()
        const password = String(body.password || '')
        if (!username || !email || !password) {
            return json({error: '邮箱、用户名或密码无效。'}, {status: 400})
        }
        if (findUser(username)) {
            return json({error: '该用户名已被注册。'}, {status: 409})
        }
        const now = Date.now()
        const user: IUser = {
            id: Utils.createGuid(IDType.User),
            username,
            email,
            nickname: '',
            firstname: username,
            lastname: '',
            props: {},
            create_at: now,
            update_at: now,
            is_bot: false,
            is_guest: false,
            roles: 'system_user',
        }
        addUser(user, password)
        return json({user}, {status: 200})
    }),

    http.get('/api/v2/users/me', ({request}) => {
        const me = authed(request)
        return me ? json(me) : unauthorized()
    }),

    http.get('/api/v2/clientConfig', () => json(defaultClientConfig)),

    // ---- teams ------------------------------------------------------------

    http.get('/api/v2/teams', ({request}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        return json([{id: TEAM_ID, title: '顶峰科技', description: '演示工作区'}])
    }),

    // ---- boards -----------------------------------------------------------

    http.get('/api/v2/teams/:teamId/boards', ({request, params}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        return json(boardsOfTeam(String(params.teamId)))
    }),

    http.get('/api/v2/teams/:teamId/templates', ({request, params}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        return json(boardsOfTeam(String(params.teamId), true))
    }),

    http.post('/api/v2/teams/:teamId/boards', async ({request, params}) => {
        const me = authed(request)
        if (!me) {
            return unauthorized()
        }
        const body = await readBody(request)
        const now = Date.now()
        const board = createBoard({
            ...(body as unknown as Board),
            teamId: String(params.teamId),
            createdBy: me.id,
            modifiedBy: me.id,
            createAt: now,
            updateAt: now,
        })
        board.id = board.id || Utils.createGuid(IDType.Board)
        upsertBoard(board)
        return json(board, {status: 201})
    }),

    /** Bulk patch: {board_ids, board_patches}, same payload as the server. */
    http.patch('/api/v2/teams/:teamId/boards', async ({request}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        const body = await readBody(request)
        const ids: string[] = body.board_ids || []
        const patches: any[] = body.board_patches || []
        ids.forEach((id, i) => patchBoard(id, patches[i] || {}))
        return json(true)
    }),

    http.get('/api/v2/boards/:boardId', ({request, params}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        const board = findBoard(String(params.boardId))
        return board ? json(board) : notFound('board-not-found')
    }),

    http.patch('/api/v2/boards/:boardId', async ({request, params}) => {
        const me = authed(request)
        if (!me) {
            return unauthorized()
        }
        const patch = await readBody(request)
        const board = patchBoard(String(params.boardId), {...(patch as any), modifiedBy: me.id})
        return board ? json(true) : notFound('board-not-found')
    }),

    http.delete('/api/v2/boards/:boardId', ({request, params}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        return deleteBoard(String(params.boardId)) ? json(true) : notFound('board-not-found')
    }),

    http.post('/api/v2/boards/:boardId/undelete', ({request, params}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        const board = patchBoard(String(params.boardId), {deleteAt: 0})
        return board ? json(true) : notFound('board-not-found')
    }),

    http.post('/api/v2/boards/search', async ({request}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        const term = new URL(request.url).searchParams.get('q') || ''
        const all = boardsOfTeam(TEAM_ID)
        const needle = term.trim().toLowerCase()
        const boards = needle ? all.filter((b) => `${b.title} ${b.description}`.toLowerCase().includes(needle)) : []
        return json({boards, total: boards.length})
    }),

    // ---- blocks -----------------------------------------------------------

    http.get('/api/v2/teams/:teamId/blocks', ({request}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        return json(teamBlocks(request))
    }),

    http.post('/api/v2/teams/:teamId/blocks', async ({request}) => {
        const me = authed(request)
        if (!me) {
            return unauthorized()
        }
        const body = await readBody(request)
        const now = Date.now()
        const block = createBlock({
            ...(body as unknown as Block),
            createdBy: me.id,
            modifiedBy: me.id,
            createAt: now,
            updateAt: now,
        })
        block.id = block.id || Utils.createBlockId(block.type)
        return json(insertBlock(block), {status: 201})
    }),

    /** Bulk patch: {block_ids, block_patches}. */
    http.patch('/api/v2/teams/:teamId/blocks', async ({request}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        const body = await readBody(request)
        const ids: string[] = body.block_ids || []
        const patches: any[] = body.block_patches || []
        ids.forEach((id, i) => patchBlock(id, patches[i] || {}))
        return json(true)
    }),

    http.get('/api/v2/boards/:boardId/blocks', ({request, params}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        const url = new URL(request.url)
        const boardId = String(params.boardId)
        const blockId = url.searchParams.get('block_id')
        if (blockId) {
            return json(queryBlocks({blockId}))
        }
        const all = url.searchParams.get('all') === 'true'
        const parentId = url.searchParams.get('parent_id') || undefined
        const type = url.searchParams.get('type') || undefined
        return json(queryBlocks({boardId, parentId, type, includeDeleted: all}))
    }),

    http.patch('/api/v2/boards/:boardId/blocks/:blockId', async ({request, params}) => {
        const me = authed(request)
        if (!me) {
            return unauthorized()
        }
        const patch = await readBody(request)
        const block = patchBlock(String(params.blockId), patch as any)
        if (block) {
            block.modifiedBy = me.id
        }
        return block ? json(true) : notFound('block-not-found')
    }),

    http.delete('/api/v2/boards/:boardId/blocks/:blockId', ({request, params}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        return deleteBlock(String(params.blockId)) ? json(true) : notFound('block-not-found')
    }),

    http.post('/api/v2/boards/:boardId/blocks/:blockId/undelete', ({request, params}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        return undeleteBlock(String(params.blockId)) ? json(true) : notFound('block-not-found')
    }),

    // ---- sidebar categories ----------------------------------------------

    http.get('/api/v2/teams/:teamId/categories', ({request, params}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        return json(categoriesOfTeam(String(params.teamId)))
    }),

    http.post('/api/v2/teams/:teamId/categories', async ({request, params}) => {
        const me = authed(request)
        if (!me) {
            return unauthorized()
        }
        const body = await readBody(request)
        const now = Date.now()
        const category: CategoryBoards = {
            id: String(body.id || Utils.createGuid(IDType.None)),
            name: String(body.name || '未命名'),
            userID: me.id,
            teamID: String(params.teamId),
            createAt: now,
            updateAt: now,
            deleteAt: 0,
            collapsed: Boolean(body.collapsed),
            sortOrder: Number(body.sortOrder ?? 9999),
            type: body.type === 'system' ? 'system' : 'custom',
            isNew: Boolean(body.isNew),
            boardMetadata: (body.boardMetadata as CategoryBoards['boardMetadata']) || [],
        }
        return json(upsertCategory(category), {status: 201})
    }),

    http.patch('/api/v2/teams/:teamId/categories/:categoryId', async ({request, params}) => {
        const me = authed(request)
        if (!me) {
            return unauthorized()
        }
        const body = await readBody(request)
        const existing = categoriesOfTeam(String(params.teamId)).find((c) => c.id === String(params.categoryId))
        if (!existing) {
            return notFound('category-not-found')
        }
        const next: CategoryBoards = {
            ...existing,
            ...body,
            boardMetadata: (body.boardMetadata as CategoryBoards['boardMetadata']) || existing.boardMetadata,
            updateAt: Date.now(),
        }
        return json(upsertCategory(next))
    }),

    // ---- users (person property lookups) ---------------------------------

    http.get('/api/v2/teams/:teamId/users', ({request}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        const needle = (new URL(request.url).searchParams.get('search') || '').trim().toLowerCase()
        const users = allUsers().
            filter((u) => !needle || `${u.username} ${u.firstname} ${u.lastname} ${u.nickname}`.toLowerCase().includes(needle))
        return json(users)
    }),

    // ---- clone-only helper ------------------------------------------------

    http.post('/api/v2/mock/reset', ({request}) => {
        if (!authed(request)) {
            return unauthorized()
        }
        resetDatabase()
        return json(true)
    }),
]
